import React, { useState } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faClock,
  faFileSignature,
  faPaperPlane,
  faUserCheck,
  faXmark,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { OfferApplicantStatus } from './../../../../types/OfferApplicantStatus.interface';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './CandidateActionBar.translations.json';
import { IrreversibleActionModal, type CandidateAction } from './IrreversibleActionModal';

type SupportedLanguage = keyof typeof translations;
type ActionLabelKey = Exclude<keyof (typeof translations)['en'], 'actions' | 'cancel'>;

interface CandidateActionBarProps {
  onValidate: () => void;
  onPending: () => void;
  onReject: () => void;
  onOfferSent?: () => void;
  onContractSent?: () => void;
  onHired?: () => void;
  status: OfferApplicantStatus;
}

interface ActionConfig {
  action: CandidateAction;
  labelKey: ActionLabelKey;
  icon: IconDefinition;
  variant: 'contained' | 'outlined';
  sx: SxProps<Theme>;
  visibleFor: OfferApplicantStatus[];
  isDecision: boolean;
}

const BASE_BUTTON_SX = { textTransform: 'none', fontWeight: 'bold' } as const;
const DECISION_STATUSES = [
  OfferApplicantStatus.SHORTLISTED_BY_TECHNICAL_QUESTIONNAIRE,
  OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW,
  OfferApplicantStatus.REJECTED_AFTER_FINAL_REVIEW,
  OfferApplicantStatus.UNDER_FINAL_REVIEW,
];
const LOCKED_DECISION_STATUSES = [
  OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW,
  OfferApplicantStatus.REJECTED_AFTER_FINAL_REVIEW,
  OfferApplicantStatus.UNDER_FINAL_REVIEW,
];

const primarySx = (color: string, hover: string, textColor = '#FFFFFF') => ({
  ...BASE_BUTTON_SX,
  backgroundColor: color,
  color: textColor,
  '&:hover': { backgroundColor: hover },
});

const ACTIONS: ActionConfig[] = [
  {
    action: 'validate',
    labelKey: 'ok',
    icon: faCheck,
    variant: 'contained',
    sx: primarySx('#0a66c2', '#084298', '#000000'),
    visibleFor: DECISION_STATUSES,
    isDecision: true,
  },
  {
    action: 'pending',
    labelKey: 'pending',
    icon: faClock,
    variant: 'outlined',
    sx: { ...BASE_BUTTON_SX, fontWeight: 'normal', color: '#FFFFFF', borderColor: '#333742', '&:hover': { borderColor: '#555C6E' } },
    visibleFor: DECISION_STATUSES,
    isDecision: true,
  },
  {
    action: 'reject',
    labelKey: 'reject',
    icon: faXmark,
    variant: 'contained',
    sx: primarySx('#E55353', '#D44242'),
    visibleFor: DECISION_STATUSES,
    isDecision: true,
  },
  {
    action: 'offerSent',
    labelKey: 'offerSent',
    icon: faPaperPlane,
    variant: 'contained',
    sx: primarySx('#7c3aed', '#6d28d9'),
    visibleFor: [OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW],
    isDecision: false,
  },
  {
    action: 'contractSent',
    labelKey: 'contractSent',
    icon: faFileSignature,
    variant: 'contained',
    sx: primarySx('#0d9488', '#0f766e'),
    visibleFor: [OfferApplicantStatus.OFFER_ACCEPTED_BY_CANDIDATE],
    isDecision: false,
  },
  {
    action: 'hired',
    labelKey: 'hired',
    icon: faUserCheck,
    variant: 'contained',
    sx: primarySx('#16a34a', '#15803d'),
    visibleFor: [OfferApplicantStatus.CONTRACT_SENT],
    isDecision: false,
  },
];

export const CandidateActionBar: React.FC<CandidateActionBarProps> = ({
  onValidate,
  onPending,
  onReject,
  onOfferSent,
  onContractSent,
  onHired,
  status,
}) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;

  const [cancelDecisionClicked, setCancelDecisionClicked] = useState<boolean>(false);
  const [submittedAction, setSubmittedAction] = useState<CandidateAction | null>(null);
  const [pendingAction, setPendingAction] = useState<CandidateAction | null>(null);

  const handlers: Record<CandidateAction, (() => void) | undefined> = {
    validate: onValidate,
    pending: onPending,
    reject: onReject,
    offerSent: onOfferSent,
    contractSent: onContractSent,
    hired: onHired,
  };

  const isDecisionLocked = !cancelDecisionClicked && LOCKED_DECISION_STATUSES.includes(status);
  const submittedIsDecision = ACTIONS.some(({ action, isDecision }) => isDecision && action === submittedAction);
  const displayCancelDecision = submittedIsDecision || (submittedAction === null && isDecisionLocked);

  const handleConfirmAction = () => {
    if (!pendingAction) {
      return;
    }
    handlers[pendingAction]?.();
    setSubmittedAction(pendingAction);
    setPendingAction(null);
  };

  const handleCancelDecision = () => {
    setSubmittedAction(null);
    setCancelDecisionClicked(true);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mt: 3,
        backgroundColor: '#121316',
        border: '1px solid #23262d',
        borderRadius: 2,
      }}
    >
      <Typography variant="caption" fontWeight="bold" color="#0a66c2" display="block" mb={2} sx={{ letterSpacing: 1 }}>
        {content.actions}
      </Typography>
      <Box display="flex" gap={2} flexWrap="wrap">
        {ACTIONS.filter(({ visibleFor }) => visibleFor.includes(status)).map(
          ({ action, labelKey, icon, variant, sx, isDecision }) => (
            <Button
              key={action}
              variant={variant}
              onClick={() => setPendingAction(action)}
              disabled={submittedAction !== null || (isDecision && isDecisionLocked)}
              startIcon={<FontAwesomeIcon icon={icon} />}
              sx={sx}
            >
              {content[labelKey]}
            </Button>
          ),
        )}
        {displayCancelDecision && (
          <Button
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faXmark} />}
            color="warning"
            onClick={handleCancelDecision}
            sx={{
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            {content.cancel}
          </Button>
        )}
      </Box>
      <IrreversibleActionModal
        open={pendingAction !== null}
        action={pendingAction ?? 'validate'}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
      />
    </Paper>
  );
};